import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import classNames from "classnames";
import { Button } from "@/components/Button";
import { LanguageSelector } from "@/components/LanguageSelector";
import { LanguageCard } from "@/components/LanguageCard";
import sendolaPayBusinessWhite from "@/assets/Img/sendolapay-business-logo-white.png";
import sendolaPayBusiness from "@/assets/Img/sendolapay-business-logo.png";
import HubspotContactForm from "@/components/HubspotContactForm";
import { Modal } from "@/components/Modal";
import { Link } from "react-router-dom";

const menuItem =
  "flex w-full gap-2 items-center py-3 hover:text-primary-300 text-white cursor-pointer border-b-[1px] border-b-gray-600 last:border-b-0";
const desktopMenuItem =
  "flex w-full gap-2 items-center py-2 px-4 text-white hover:text-business-green cursor-pointer border-b-gray-600 first:pt-4 last:pb-4";
const navItem =
  "text-sm xl:text-base hover:text-gray-300 flex-shrink-0 text-white font-[500]";
const homeNavItem =
  "text-sm xl:text-base hover:text-primary-700 flex-shrink-0 text-black font-[500]";

export const BusinessHeader = () => {
  const [t, i18n] = useTranslation("global");
  const [tBusiness] = useTranslation("business");
  const currentLanguage = i18n.language;
  const pathname = window.location?.pathname;
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [menuTouched, setMenuTouched] = useState<boolean>(false);
  const [showLanguageBar, setShowLanguageBar] = useState<boolean>(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState<boolean>(false);
  const [verticalScrolled, setVerticalScrolled] = useState(false);
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    useState<boolean>(false);
  const [loadingForm, setLoadingForm] = useState<boolean>(true);

  const ref = useOutsideClick(() => {
    if (menuOpen) setMenuOpen(false);
  });
  const menuRef = useOutsideClick(() => {
    if (showOptionsMenu) setShowOptionsMenu(false);
  });
  const isHome = useMemo(() => {
    if (pathname === "/") return true;
    return false;
  }, [pathname]);

  const navClassNames = useMemo(
    () => (isHome && !verticalScrolled ? homeNavItem : navItem),
    [isHome, verticalScrolled]
  );

  useEffect(() => {
    const checkVerticalScroll = () => {
      const scrollY = window.scrollY;
      setVerticalScrolled(scrollY > 60);
    };

    window.addEventListener("scroll", checkVerticalScroll);

    return () => {
      window.removeEventListener("scroll", checkVerticalScroll);
    };
  }, []);

  useEffect(() => {
    if (!currentLanguage) setShowLanguageBar(true);
  }, [currentLanguage]);

  return (
    <>
      <nav className="z-50 sticky top-0 transition-all duration-200 ease-in">
        <div
          className={classNames(
            "w-full flex justify-between items-center transition-all bg-gradient-to-r from-black via-sendolab-gray2 to-black",
            {
              "h-16 px-4 py-2": true, // Mobile styles
              "lg:px-8 lg:h-20": true, // Desktop styles
            }
          )}
        >
          <div className="flex gap-4 md:gap-10 items-center flex-shrink-0">
            <a href="#top">
              <img
                className="h-9 sm:h-10 md:h-12 xl:h-14 w-auto object-contain select-none"
                src={sendolaPayBusinessWhite}
                alt="SendolaPay Business Logo"
              />
            </a>

            <div className="flex h-full items-center gap-2 xs:gap-6">
              <Link to="/">
                <h1 className={navClassNames}>{t("Header.Personal")}</h1>
              </Link>
              <Link to="/business">
                <h1
                  className={classNames(navClassNames, "!text-business-green")}
                >
                  {t("Header.Business")}
                </h1>
              </Link>
            </div>
          </div>

          <div
            ref={ref}
            className="flex justify-end flex-shrink-0 lg:hidden w-fit ml-4"
          >
            <LanguageSelector
              blackText={!verticalScrolled && isHome}
              onClick={() => setShowLanguageBar(!showLanguageBar)}
            />
            <Bars3Icon
              className={classNames(
                "w-10 h-10 rounded cursor-pointer hover:bg-gray-900/30 p-2",
                {
                  "bg-gray-900": menuTouched,
                  "text-white": verticalScrolled || !isHome,
                  "text-black": !verticalScrolled && isHome,
                }
              )}
              onClick={() => setMenuOpen(!menuOpen)}
              onTouchStart={() => setMenuTouched(true)}
              onTouchEnd={() => setMenuTouched(false)}
            />
          </div>

          <div className="hidden relative h-full lg:flex items-center gap-4">
            <div
              className={
                "px-4 py-1 rounded-lg border-[1px] border-transparent hover:bg-gray-500/10 cursor-pointer select-none"
              }
              onMouseEnter={() => setShowOptionsMenu(true)}
              onMouseLeave={() => setShowOptionsMenu(false)}
            >
              <h1
                ref={menuRef}
                className={classNames({
                  "text-white hover:border-gray-500":
                    verticalScrolled || !isHome,
                  "text-black hover:border-gray-300":
                    !verticalScrolled && isHome,
                })}
              >
                {tBusiness("Header.Solutions")}
              </h1>

              <div
                className={classNames(
                  "w-fit min-w-52 max-w-80 flex flex-col rounded-lg bg-gradient-to-r from-stone-950 via-stone-900 to-stone-950 absolute top-[3.4rem] left-0 shadow-lg overflow-hidden transition-all",
                  {
                    "max-h-0 border-none": !showOptionsMenu,
                    "max-h-96": showOptionsMenu,
                  }
                )}
              >
                <a href="/business#global-reach" className={desktopMenuItem}>
                  <p className="text-sm">{tBusiness("Header.GlobalReach")}</p>
                </a>
                <a href="/business#savings" className={desktopMenuItem}>
                  <p className="text-sm">{tBusiness("Header.Savings")}</p>
                </a>
                <a href="/business#become-a-member" className={desktopMenuItem}>
                  <p className="text-sm">{tBusiness("Header.BecomeMember")}</p>
                </a>
                <a href="/business#premium-service" className={desktopMenuItem}>
                  <p className="text-sm">
                    {tBusiness("Header.PremiumService")}
                  </p>
                </a>
                <a href="/business#faqs" className={desktopMenuItem}>
                  <p className="text-sm">{tBusiness("Header.FAQs")}</p>
                </a>
              </div>
            </div>

            <div className="flex gap-2">
              <LanguageSelector
                className="hidden lg:block"
                onClick={() => setShowLanguageBar(!showLanguageBar)}
              />
              <Button
                className="!bg-black !text-business-green !border-[2px] !border-business-green !px-8 hover:!bg-stone-900 hover:!opacity-100"
                size="sm"
                text={tBusiness("Buttons.CreateAccount")}
                onClick={() => setIsCreateAccountModalOpen(true)}
              />
              <Button
                className="bg-white !border-0 !px-8 !text-black hover:bg-gray-200 !font-light"
                variant="secondary"
                size="sm"
                text={tBusiness("Buttons.ContactUs")}
                link="/business/contact"
              />
            </div>
          </div>
        </div>

        <div
          className={classNames(
            "absolute overflow-hidden transition-all w-max min-w-60 rounded-b-lg flex-col bg-gradient-to-r from-sendolab-gray4 via-sendolab-gray2 to-sendolab-gray4 shadow-lg px-4 top-16 right-0 z-20 box-border",
            {
              "max-h-80": menuOpen,
              "max-h-0": !menuOpen,
            }
          )}
        >
          <a href="/business#global-reach" className={menuItem}>
            <p className="text-sm lg:text-base">
              {tBusiness("Header.GlobalReach")}
            </p>
          </a>
          <a href="/business#savings" className={menuItem}>
            <p className="text-sm lg:text-base">
              {tBusiness("Header.Savings")}
            </p>
          </a>
          <a href="/business#become-a-member" className={menuItem}>
            <p className="text-sm lg:text-base">
              {tBusiness("Header.BecomeMember")}
            </p>
          </a>
          <a href="/business#premium-service" className={menuItem}>
            <p className="text-sm lg:text-base">
              {tBusiness("Header.PremiumService")}
            </p>
          </a>
          <a href="/business#faqs" className={menuItem}>
            <p className="text-sm lg:text-base">{tBusiness("Header.FAQs")}</p>
          </a>
        </div>
      </nav>

      <LanguageCard
        show={showLanguageBar}
        onClose={() => setShowLanguageBar(false)}
      />

      <Modal
        withClose
        open={isCreateAccountModalOpen}
        onClose={() => {
          setIsCreateAccountModalOpen(false);
          setLoadingForm(true);
        }}
      >
        <div className="w-full flex flex-col items-center">
          <div className="w-full pb-8 flex justify-between items-center">
            <img
              className="h-9 sm:h-10 md:h-12 xl:h-14 w-auto object-contain select-none"
              src={sendolaPayBusiness}
              alt="SendolaPay Business Logo"
            />
          </div>
          {loadingForm && (
            <p className="w-full text-center text-black font-semibold">
              {t("Loading")}...
            </p>
          )}
          <HubspotContactForm
            region="na1"
            portalId="44207752"
            formId="db94a133-c8f4-47bd-9c41-fdefdc87209a"
            onReady={() => {
              setLoadingForm(false);
            }}
          />
        </div>
      </Modal>
    </>
  );
};
